import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel
} from "@/components/ui/form"
import { Dropdown } from '../ui/filterSelect';
import { DatePickerInput } from '../ui/date';
import { OrderInfoSchema } from '../order/OrderSchema';
import { BookingSchema, BookingInfoSchema } from './BookingSchema';

import { set, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";

import { SiteSchema } from '../site/SiteSchema';
import { Popup } from "../common/Popup";
import { useApiFetch } from "@/helper/apiFetch";
import { useTranslate } from "@/helper/translate";
import { SpotInfo } from "../spot/SpotInfo";
import { SpotInfoDisplaySchema } from "../spot/SpotSchema";
type BookingInfoProps = {
  booking: BookingInfoSchema;
  customerId?: string;
  siteId?: string;
  onClose: (booking?: BookingInfoSchema) => void;
  onDeleted: () => void;
};

type Lookup = { id: string; name: string };

const now = new Date();



export function BookingInfo({ booking, customerId, siteId,  onClose, onDeleted }: BookingInfoProps) {

  const apiFetch = useApiFetch();
  const { translate } = useTranslate();
  const [orders, setOrders] = useState<OrderInfoSchema[]>([]);
  const [spots, setSpots] = useState<Lookup[]>([]);
  const [errorText, setError] = useState("");
  const [warningText, setWarning] = useState("");
  const [returnValue, setReturnValue] = useState<BookingInfoSchema>();
  const [isDelete, prepareDelete] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [defaultCustomerID, setCustomerId] = useState(customerId);
  const [displaySpotId, setDisplaySpotId] = useState<string | null>(null);

  // end of current month, or end of next month if today is already the last day of the month
  const beginDate = new Date(booking.bookingBegin);
const endOfCurrentMonth = new Date(beginDate.getFullYear(), beginDate.getMonth() + 1, 0);
const endOfMonth = beginDate.getDate() === endOfCurrentMonth.getDate()
  ? new Date(beginDate.getFullYear(), beginDate.getMonth() + 2, 0)
  : endOfCurrentMonth;

  const form = useForm<z.infer<typeof BookingSchema>>({
    resolver: zodResolver(BookingSchema), defaultValues: {
      bookingId: booking.bookingId ?? "00000000-0000-0000-0000-000000000000",
      spotId: booking.spotId ?? "00000000-0000-0000-0000-000000000000",
      bookingBegin: booking.bookingBegin ?? null,
      bookingEnd: (booking.bookingEnd && new Date(booking.bookingEnd) > now)?endOfMonth : (booking.bookingEnd ?? null),
      orderId: booking.orderId ?? "00000000-0000-0000-0000-000000000000",
      customerId:  customerId ?? "00000000-0000-0000-0000-000000000000"
    }
  });


  const getValidDate = (key: keyof z.infer<typeof BookingSchema>): Date | null => {
    let date = form.getValues(key) as Date | null;
    //if (!date)
    //  return null;
    if (form.getValues("orderId")) {
      let d = orders.find(s => s.orderId == form.getValues("orderId"));

      if (key == "bookingBegin" && d?.orderBegin) {
        if (d && date && new Date(d.orderBegin) > (date as Date))
          date = new Date(d.orderBegin);

      }
      else if (key == "bookingEnd" && d?.orderEnd) {
        if (d && date && new Date(d.orderEnd) < (date as Date))
          date = new Date(d.orderEnd);
        else if (d && date == null)
          date = new Date(d.orderEnd);
      }

    }
    if (date && date < new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0))
      date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0); // prevent past dates

    console.log(`Validating date for ${key}:`, date);
    return date;
  }



const addSpot = (spot: SpotInfoDisplaySchema) => {
  console.log("Adding spot to dropdown", spot);
  //check if spot already exists in dropdown
  if (!spots.some(s => s.id == spot.id)) {
  console.log("Adding spot to dropdown 2");

    const newSpot = { id: spot.id, name: spot.name };
    setSpots(prev => [...prev, newSpot]);
    form.setValue("spotId", spot.id);
  }
}


  const deleteBooking = async () => {


    const res = await apiFetch(`/App/DeleteBooking?bookingId=${booking.bookingId}`, {
      method: "GET",

    });

    if (!res.ok) {
      const e = await res.text();
      setError(e);

    }
    else {

      onDeleted();
    }


  };


  const onSubmit = async (values: z.infer<typeof BookingSchema>) => {

    try {
      setError("");
      setWarning("");
      const res = await apiFetch(`/App/SaveBooking`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!res.ok) {
        const e = await res.text();
        setError(translate("ui.failedsave") + e);

      }
      else {

        const data = await res.json();
        let warning = "";
        if (data.warnings && data.warnings.length > 0) {
          warning += '\n' + data.warnings;
        }

        if (data.sites && data.sites.length > 0) {
          const lines = data.sites.map((s: any) => `${s.name}: ${s.settings}`);
          warning += `\n${translate("ui.spotpersiteerror")}\n\n${lines.join('\n')}`;

        }
        if (warning != "") {
          setWarning(warning);
          setReturnValue(data.booking as BookingInfoSchema);
        }
        else
          onClose(data.booking as BookingInfoSchema);

      }
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (defaultCustomerID == undefined)
      return;

    apiFetch(`/App/GetSpotLookup?customerId=${defaultCustomerID ?? ""}`)
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.json();
      })
      .then(data => {
        setSpots(data);


      })
      .catch(err => {
        console.error(err);
        //setLoading(false);
      });
  }, [defaultCustomerID]);


  useEffect(() => {
    //this screen comes from booking AND Playlist, Playlist does not forward customerid!
    // if (spots.length == 0 && defaultCustomerID != undefined) {
    //   return;
    // } new customers have no spots 


    if (booking.bookingId == null) {
      //load dropdowns
      apiFetch(`/App/GetOrderLookup?customerId=${defaultCustomerID ?? ""}`)
        .then(res => {
          if (!res.ok) throw new Error("Failed to fetch");
          return res.json();
        })
        .then(data => {
          let orders = data as OrderInfoSchema[];
          setOrders(orders.filter((s) => s.siteId == siteId));
          
            const rows = orders//.filter((s) => s.siteId !== siteId)
              .map((s) => ({ siteId: s.siteId, name: s.site, selected: false, orderId: s.orderId })); 

            // Validate & set with Zod (ensures shape is correct)
            form.setValue("sites", z.array(SiteSchema).parse(rows), { shouldDirty: true });

          

        })
        .catch(err => {
          console.error(err);
          //setLoading(false);
        });
    } else {
      apiFetch(`/App/GetBooking?Id=${booking.bookingId}`)
        .then(res => {
          if (!res.ok) throw new Error("Failed to fetch");
          return res.json();
        })
        .then(data => {

          form.reset(data);
          if (defaultCustomerID == undefined && data.customerId)
            setCustomerId(data.customerId);


          //setIsLoaded(true);
        })
        .catch(err => {
          console.error(err);
          //setLoading(false);
        });
    }
  }, [spots]);


  const selectedOrderName = orders.filter((s) => s.orderId == form.watch("orderId"))[0]?.orderName;
  const additionalSites = form.watch("sites")?.filter((s) => selectedOrderName && ((s.name?.indexOf(selectedOrderName) ?? -1) >= 0 ) && s.orderId != form.getValues("orderId")) ?? [];  // subscribe
  const additionalSitesAll = form.watch("sites")?.filter((s) => selectedOrderName  && s.orderId != form.getValues("orderId")) ?? [];  // subscribe
  return (
    <div className="popCnt p-0">

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, (error) => { console.log("Validation errors:", error); })} className="space-y-2">

        <div className="bg-white dark:bg-zinc-600 rounded-2xl shadow-lg space-y-4 mx-[-10px] my-1 p-3">

          {(booking.bookingId == null) ? (
            <FormField
              control={form.control}
              name="orderId"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>{translate("ui.order")}</FormLabel>
                  <FormControl>
                    <Dropdown
                      {...field}
                      source={orders}
                      getLabel={(x) => x.orderName + " (" + new Date(x.orderBegin).toLocaleDateString() + " - " + new Date(x.orderEnd).toLocaleDateString() + ")"}
                      getValue={(x) => x.orderId}
                      onChange={(value) => {
                        field.onChange(value); // update orderId
                        let d = getValidDate("bookingBegin");
                        if (d)
                          form.setValue("bookingBegin", d);

                        d = getValidDate("bookingEnd");
                        if (d)
                          form.setValue("bookingEnd", d);
                      }}
                    />
                  </FormControl>
                </FormItem>

              )}
            />
          ) : (
            <div>
              <label>{translate("ui.order")}</label><br/>
              <label>{form.getValues("orderDesc")}</label>
            </div>
          )}

          <div className="grid-default md:grid-cols-4  items-end">
            <FormField
              control={form.control}
              name="spotId"
              render={({ field }) => (
                <FormItem className="col-span-3">
                  <FormLabel>{translate("ui.spot")}</FormLabel>
                  <FormControl>
                    <Dropdown
                      {...field}
                      source={spots}
                      getLabel={(x) => x.name}
                      getValue={(x) => x.id}
                      searchable={true}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {form.getValues("spotId") ? (
              <Button type="button"  onClick={() => {
                  setDisplaySpotId(form.getValues("spotId"));
              }}>{translate("ui.viewspot")}</Button>
            ) : (form.getValues("orderId") && (
              <Button type="button"  onClick={() => {
                  setDisplaySpotId("NEW");
              }}>{translate("ui.createspot")}</Button>
            ))}
          </div>

          <div className="grid-default md:grid-cols-4">
            <FormField
              control={form.control}
              name="bookingBegin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{translate("ui.startdate")}</FormLabel>
                  <FormControl>
                    <DatePickerInput {...field}
                      onChange={(value) => {
                        field.onChange(value);

                        let d = getValidDate("bookingBegin");
                        if (value != d)
                          field.onChange(d);

                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="bookingEnd"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{translate("ui.enddate")}</FormLabel>
                  <FormControl>
                    <DatePickerInput {...field}
                      setMidnight={true}
                      onChange={(value) => {
                        field.onChange(value);
                        let d = getValidDate("bookingEnd");
                        if (value != d)
                          field.onChange(d);
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div>
            {form.getValues("sites")?.length > 0 && (
              <>
                <div className="mb-2 font-bold">{translate("ui.copybooking")}</div>

                {(showAll?additionalSitesAll:additionalSites)!.map((s, i) => {
                  return (
                    <div key={s.siteId} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={s.selected}
                        onChange={(e) => {
                          const i2 = form.getValues("sites")!.findIndex(x => x.orderId == s.orderId);
                          if (i2 >= 0)
                            form.setValue(`sites.${i2}.selected`, e.target.checked, { shouldDirty: true })
                        }
                        }
                      />
                      <label>{s.name}</label>
                    </div>
                  );
                })}
                {!showAll && additionalSitesAll.length > additionalSites.length && (
                  <div className="flex items-center gap-2">
                    <Button
                    className="max-h-[20px] text-sm mt-2"
                      onClick={() => setShowAll(true)}
                    >
                      {translate("ui.more")}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
        {/* --  -- */}


          <div className="grid gap-2 grid-cols-7 items-center">
            {(booking.bookingId != null) ? (
              <Trash2 className="w-[32px] h-[32px] cursor-pointer col-span-3 bg-rose-600 hover:bg-rose-700 text-white p-[6px] rounded-full" type="button" onClick={() => prepareDelete(true)} />
            ) : (
              <div className="col-span-3"></div>
            )}
            <Button className="w-full col-span-2" type="button" onClick={() => { console.log("Cancel clicked"); onClose(); }}>{translate("common.cancel")}</Button>

            <Button className="w-full col-span-2" type="submit">{translate("common.save")}</Button>
          </div>

          {(errorText != "") && (
            <div className="text-red-500 mt-2">
              {errorText}
            </div>
          )
          }




          <Popup
            isOpen={isDelete}
            onClose={() => prepareDelete(false)}
            title={translate("ui.deletebooking")}
            className="w-[500px]"
          >
            <div>
              <div>{translate("ui.confirmdeletebooking")}</div>
              <div className="grid gap-2 grid-cols-2 mt-4">
                <Button className="w-full" type="button" onClick={() => prepareDelete(false)}>{translate("common.cancel")}</Button>
                <Button className="w-full" type="button" variant="destructive" onClick={() => { prepareDelete(false); deleteBooking(); }}>{translate("common.delete")}</Button>


              </div>
            </div>
          </Popup>


          {warningText != "" && (
            <Popup
              isOpen={warningText != ""}
              onClose={() => onClose(returnValue)}
              title={translate("ui.warning")}
              className="w-[500px]"
            >
              <div>
                <div>{translate("ui.pleaseNote")}</div>
                <div style={{ whiteSpace: "pre-line" }}>{warningText}</div>
                <div className="text-center mt-4">
                  <Button className="w-[150px]" type="button" variant="destructive" onClick={() => { onClose(returnValue) }}>OK</Button>


                </div>
              </div>
            </Popup>
          )}




        <Popup
            isOpen={displaySpotId !== null}
            onClose={() => setDisplaySpotId(null)}
            title={translate("ui.spotinfo")}
            className="w-[80%] max-h-[90vh] overflow-auto"
            
          >
            <SpotInfo allowTemplate={false} onChanged={(spot) => { console.log('spot saved'); (displaySpotId ?? "")=="NEW"&&( addSpot(spot as SpotInfoDisplaySchema)) }}  onClose={() => {console.log('spot closed'); setDisplaySpotId(null); }} givenSpotId={(displaySpotId ?? "")=="NEW"?"":displaySpotId ?? ""} customerId={form.getValues("customerId")??""} orderId={form.getValues("orderId") ?? null} />
          </Popup>

        </form>
      </Form>
    </div>

  )
}



